import { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import {
  ICreateDV360TicketsInsertionOrderPayload,
  IDV360TicketsInsertionOrder,
  IUpdateDV360TicketsInsertionOrderPayload,
} from '@/Interfaces/DV360Tickets/InsertionOrder.interface';
import {
  ICreateDV360TicketsLineItemPayload,
  IDV360TicketsLineItem,
  IUpdateDV360TicketsLineItemPayload,
} from '@/Interfaces/DV360Tickets/LineItem.interface';
import { IProduct } from '@/Interfaces/Product.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import {
  DV360_TICKETS_AUDIENCES_ENDPOINT,
  DV360_TICKETS_INSERTION_ORDERS_ENDPOINT,
  DV360_TICKETS_LINE_ITEMS_ENDPOINT,
  DV360_TICKETS_PRODUCTS_ENDPOINT,
} from '@/constants/endpoints';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

enum actionPrefix {
  GET_INSERTION_ORDER = 'dv360TicketsCampaignManagementSlice/getInsertionOrder',
  CREATE_INSERTION_ORDER = 'dv360TicketsCampaignManagementSlice/createInsertionOrder',
  UPDATE_INSERTION_ORDER = 'dv360TicketsCampaignManagementSlice/updateInsertionOrder',
  CREATE_LINE_ITEM = 'dv360TicketsCampaignManagementSlice/createLineItem',
  UPDATE_LINE_ITEM = 'dv360TicketsCampaignManagementSlice/updateLineItem',
  GET_PRODUCTS = 'dv360TicketsCampaignManagementSlice/getProducts',
  GET_AUDIENCES = 'dv360TicketsCampaignManagementSlice/getAudiences',
}

interface IState {
  products: IProduct[];
  audiences: IAudienceControlOption<number>[];
  activeInsertionOrder: IDV360TicketsInsertionOrder | null;
  productRequestId: null | string;
  audienceRequestId: null | string;
  activeInsertionOrderRequestId: null | string;
  activeInsertionOrderError: null | string;
  createDV360TicketsLineItemRequestId: null | string;
  updateDV360TicketsLineItemRequestId: null | string;
}

const initialState: IState = {
  products: [],
  audiences: [],
  activeInsertionOrder: null,
  productRequestId: null,
  audienceRequestId: null,
  activeInsertionOrderRequestId: null,
  createDV360TicketsLineItemRequestId: null,
  updateDV360TicketsLineItemRequestId: null,
  activeInsertionOrderError: null,
};

export const dv360TicketsCampaignManagementSlice = createSlice({
  name: 'dv360TicketsCampaignManagement',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDv360TicketsInsertionOrderData.pending, pendingCaseHandler('activeInsertionOrder'))
      .addCase(getDv360TicketsInsertionOrderData.rejected, rejectedCaseHandler('activeInsertionOrder'))
      .addCase(getDv360TicketsInsertionOrderData.fulfilled, fulfilledCaseHandler('activeInsertionOrder'))
      .addCase(createDV360TicketsLineItem.pending, pendingCaseHandler('createDV360TicketsLineItem'))
      .addCase(createDV360TicketsLineItem.rejected, rejectedCaseHandler('createDV360TicketsLineItem'))
      .addCase(createDV360TicketsLineItem.fulfilled, (state, action) => {
        if (state.createDV360TicketsLineItemRequestId === action.meta.requestId) {
          state.createDV360TicketsLineItemRequestId = null;
          if (state.activeInsertionOrder) {
            state.activeInsertionOrder.lineItems = [...(state.activeInsertionOrder?.lineItems || []), action.payload];
          }
        }
      })
      .addCase(updateDV360TicketsLineItem.pending, pendingCaseHandler('updateDV360TicketsLineItem'))
      .addCase(updateDV360TicketsLineItem.rejected, rejectedCaseHandler('updateDV360TicketsLineItem'))
      .addCase(updateDV360TicketsLineItem.fulfilled, (state, action) => {
        if (state.updateDV360TicketsLineItemRequestId === action.meta.requestId) {
          state.updateDV360TicketsLineItemRequestId = null;
          if (state.activeInsertionOrder) {
            state.activeInsertionOrder.lineItems = [
              ...state.activeInsertionOrder.lineItems.map(lineItem =>
                lineItem.id === action.payload.id ? action.payload : lineItem,
              ),
            ];
          }
        }
      })
      .addCase(getDV360TicketsProducts.pending, pendingCaseHandler('products'))
      .addCase(getDV360TicketsProducts.rejected, rejectedCaseHandler('products'))
      .addCase(getDV360TicketsProducts.fulfilled, fulfilledCaseHandler('products'))
      .addCase(getDV360TicketsAudiences.pending, pendingCaseHandler('audiences'))
      .addCase(getDV360TicketsAudiences.rejected, rejectedCaseHandler('audiences'))
      .addCase(getDV360TicketsAudiences.fulfilled, fulfilledCaseHandler('audiences'));
  },
});

export default dv360TicketsCampaignManagementSlice.reducer;

export const getDv360TicketsInsertionOrderData = createAsyncThunk<
  IDV360TicketsInsertionOrder,
  { id: number },
  IRejectValueThunkConfig
>(actionPrefix.GET_INSERTION_ORDER, async (payload, { rejectWithValue }) => {
  const { id, ...params } = payload;
  const url = `${DV360_TICKETS_INSERTION_ORDERS_ENDPOINT}/${id}`;

  try {
    const { data } = await axios.get(url, { params });
    return data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});

export const createDV360TicketsInsertionOrder = createAsyncThunk<
  IDV360TicketsInsertionOrder,
  ICreateDV360TicketsInsertionOrderPayload,
  IRejectValueThunkConfig
>(actionPrefix.CREATE_INSERTION_ORDER, async (payload, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(DV360_TICKETS_INSERTION_ORDERS_ENDPOINT, payload);
    return data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});

export const updateDV360TicketsInsertionOrder = createAsyncThunk<
  IDV360TicketsInsertionOrder,
  IUpdateDV360TicketsInsertionOrderPayload,
  IRejectValueThunkConfig
>(actionPrefix.UPDATE_INSERTION_ORDER, async (payload, { rejectWithValue }) => {
  try {
    const { id, ...restPayload } = payload;
    const { data } = await axios.put(`${DV360_TICKETS_INSERTION_ORDERS_ENDPOINT}/${id}`, restPayload);
    return data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});

export const createDV360TicketsLineItem = createAsyncThunk<
  IDV360TicketsLineItem,
  ICreateDV360TicketsLineItemPayload,
  IRejectValueThunkConfig
>(actionPrefix.CREATE_LINE_ITEM, async (payload, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(DV360_TICKETS_LINE_ITEMS_ENDPOINT, payload);
    return data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});

export const updateDV360TicketsLineItem = createAsyncThunk<
  IDV360TicketsLineItem,
  IUpdateDV360TicketsLineItemPayload,
  IRejectValueThunkConfig
>(actionPrefix.UPDATE_LINE_ITEM, async (payload, { rejectWithValue }) => {
  try {
    const { id, ...restPayload } = payload;
    const { data } = await axios.put(`${DV360_TICKETS_LINE_ITEMS_ENDPOINT}/${id}`, restPayload);
    return data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});

export const getDV360TicketsProducts = createAsyncThunk<IProduct[], undefined, IRejectValueThunkConfig>(
  actionPrefix.GET_PRODUCTS,
  async (_payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(DV360_TICKETS_PRODUCTS_ENDPOINT);
      return data;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
    }
  },
);

export const getDV360TicketsAudiences = createAsyncThunk<
  IAudienceControlOption<number>[],
  undefined,
  IRejectValueThunkConfig
>(actionPrefix.GET_AUDIENCES, async (_payload, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(DV360_TICKETS_AUDIENCES_ENDPOINT);
    return AudienceTargetingService.audiencesToControlOptions(data);
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err : new Error('An unknown error occurred'));
  }
});
